import { AddFactoryInventory, AddFactoryInventoryItem, FactoryInventory, FactoryInventorySourceType } from "@/types/factoryInventory";
import { Button, Flex, Form, Input, InputNumber, Modal, notification, Select, Spin, TableProps, Typography, Upload, UploadProps } from "antd";
import { FC, Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import useFactoryEnums from "../hooks/useFactoryEnums";
import STableLayout from "@/styles/STableLayout";
import { Product } from "@/types/product";
import SText from "@/styles/SText";
import { AnyObject } from "antd/es/_util/type";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { generateTemplate, parseData } from "@/pages/Requirement/components/EditRequirement/utils";
import { productList } from "@/apis/product";
import { ProductSelectMulti } from "@/components/Selector";
import { ProductWithNum } from "@/components/Selector/ProductSelectMulti";
import { fInventoryAdd, fInventoryAdjust, fInventoryCount, fInventoryList } from "@/apis/factoryInventory";
import { numFormmater } from "@/pages/Requirement/util";
import SelectFactory from "./SelectFactory";

export type AddType = "COUNT" | "ADD" | "ADJUST";
interface DataType extends AddFactoryInventoryItem {
  product: Product;
  stockCount?: number;
};

interface AddFacInventoryProps {
  open: AddType;
  onSuccess: (data: FactoryInventory[]) => void;
  onClose: () => void;
}
const AddFacInventory: FC<AddFacInventoryProps> = ({ open, onSuccess, onClose }) => {
  const { t } = useTranslation();
  const { SourceTypeList } = useFactoryEnums();
  const [type, setType] = useState<FactoryInventorySourceType>(FactoryInventorySourceType.MANUAL);
  const [desc, setDesc] = useState<string>('');
  const [items, setItems] = useState<DataType[]>([]);
  const [openProductSelect, setOpenProductSelect] = useState(false);
  const [uploading, setUploading] = useState<boolean>(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [errorCodes, setErrorCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const handleDeleteSelected = () => {
    setItems(items.filter(p => !selectedRowKeys.includes(p.productId)));
    setSelectedRowKeys([]);
  };

  const handleAddProducts = (selectedProducts: ProductWithNum[]) => {
    let resItemsMap: Record<string, DataType> = {};
    items.forEach(i => {
      resItemsMap[i.productId] = { ...i }
    });

    selectedProducts.forEach(addInfo => {
      if (!!!resItemsMap[addInfo.id]) {
        resItemsMap[addInfo.id] = {
          product: addInfo,
          count: 0,
          productId: addInfo.id
        }
      }
    });
    setItems([...Object.values(resItemsMap)]);
  }
  // 导入数据
  const handleImport: UploadProps['beforeUpload'] = async (file) => {
    setUploading(true);
    try {
      const addList = await parseData(file, t);
      if (addList.length > 0) {
        if (open === "ADJUST") {
          const stockList = await fInventoryList({ strs: addList.map(a => a.product.code) });
          const stockListMap: Record<string, FactoryInventory> = {};
          (stockList.body || []).forEach(p => {
            stockListMap[p.product.code] = { ...p }
          });
          let notProsList: string[] = [];
          for (let index = 0; index < addList.length; index++) {
            let code = addList[index].product.code
            if (!!stockListMap[code]) {
              addList[index].product = { ...stockListMap[code].product }
              addList[index].extCount = stockListMap[code].count    //占用extCount为库存
            } else {
              addList[index].extCount = 0    //占用extCount为库存
            }
          }
          if (notProsList.length > 0) {
            setErrorCodes(notProsList)
            notification.error({
              message: t('apiError.error31'),
              description: notProsList.join(', ')
            })
          }
          let resItemsMap: Record<string, DataType> = {};
          items.forEach(i => {
            resItemsMap[i.productId] = { ...i }
          });
          addList.forEach(addInfo => {
            if (!!!resItemsMap[addInfo.product.id]) {
              resItemsMap[addInfo.product.id] = {
                product: addInfo.product,
                count: addInfo.useAmount,
                productId: addInfo.product.id,
                stockCount: addInfo.extCount
              }
            }
          });
          setItems(Object.values(resItemsMap));
          notification.success({ placement: 'top', message: t("requirement.parseDataSuccess", { count: addList.length }) });
        } else {
          const productListRes = await productList({ params: { strs: addList.map(a => a.product.code) } });
          const productListMap: Record<string, Product> = {};
          productListRes.body.forEach(p => {
            productListMap[p.code] = { ...p }
          });
          let notProsList: string[] = [];
          for (let index = 0; index < addList.length; index++) {
            let code = addList[index].product.code
            if (!!productListMap[code]) {
              addList[index].product = { ...productListMap[code] }
            } else notProsList.push(code)
          }
          if (notProsList.length > 0) {
            setErrorCodes(notProsList)
            notification.error({
              message: t('apiError.error26'),
              description: notProsList.join(', ')
            })
          }
          let resItemsMap: Record<string, DataType> = {};
          items.forEach(i => {
            resItemsMap[i.productId] = { ...i }
          });

          addList.forEach(addInfo => {
            if (resItemsMap[addInfo.product.id]) {
              resItemsMap[addInfo.product.id].count += addInfo.useAmount
            } else {
              resItemsMap[addInfo.product.id] = {
                product: addInfo.product,
                count: addInfo.useAmount,
                productId: addInfo.product.id
              }
            }
          });
          setItems(Object.values(resItemsMap));
          notification.success({ placement: 'top', message: t("requirement.parseDataSuccess", { count: addList.length }) });
        }
      }
    } catch (error) {
      notification.error({ placement: 'top', message: t("requirement.invalidTemplate") });
    } finally {
      setUploading(false);
    }
    return false; // 阻止自动上传
  };
  // 下载模版
  const handleSaveTemplate = () => {
    generateTemplate(t, t('warehouse.productInput'));
  };

  const handleOk = async () => {
    if (loading) return;
    if (!!!items.find(p => p.count > 0)) return notification.error({ message: t('product.selProduct') });
    let itemInfo: AddFactoryInventoryItem[] = items.map(i => ({ productId: i.productId, count: open === "ADJUST" ? 0 - i.count : i.count }));

    let data: AddFactoryInventory = {
      sourceId: '',
      sourceType: type,
      desc,
      items: itemInfo
    };

    try {
      setLoading(true);
      let res: FactoryInventory[] = [];
      if (open === "ADD") {
        res = await fInventoryAdd(data);
        res = res.map(r => ({
          ...r,
          product: items.find(p => p.productId === r.productId)!.product
        }));
        notification.success({ message: t('warehouse.orderSuccess') });
      }
      if (open === "ADJUST") {
        res = await fInventoryAdjust(data);
        notification.success({ message: t('warehouse.outOK') });
      }
      if (open === "COUNT") {
        res = await fInventoryCount(data);
        notification.success({ message: t('counting.countingFinish') });
      }
      setLoading(false);
      onSuccess(res);
    } catch (error: any) {
      let description = '';
      if (error?.code === 70873) {
        let product = items.find(p => p.productId === error?.extra?.productId)?.product!
        description = t('apiError.error31') + ': ' + product.code
      }
      notification.error({ message: t(open === "ADD" ? 'common.addError' : open === "ADJUST" ? 'warehouse.outFail' : 'counting.executeFailed'), description })
      setLoading(false);
    }
  };

  const handleAddOut = (data: FactoryInventory[]) => {
    console.log(">>>>", data);
    let newItems: DataType[] = [];
    data.forEach(val => {
      if (!!!items.find(i => i.productId === val.productId)) {
        newItems.push({
          product: val.product,
          productId: val.productId,
          count: 0,
          stockCount: val.count
        })
      }
    })
    setItems(last => [...last, ...newItems])
    setOpenProductSelect(false);
  }

  const columns: TableProps<DataType>['columns'] = [
    {
      title: <SText text={t("product.code")} />,
      key: "productCode",
      width: 120,
      render: (_, record) => <SText style={{ width: 120, color: errorCodes.includes(record.product.code) ? "red" : "unset" }} text={record.product.code} />,

    },
    {
      title: <SText text={t("product.name")} />,
      key: "productName",
      width: 150,
      render: (_, record) => (
        <SText
          text={record.product?.name || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("sale.prodMod")} />,
      key: "productModel",
      width: 120,
      render: (_, record) => (
        <SText
          text={record.product?.model || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("product.uom")} />,
      key: "uom",
      width: 80,
      align: "center",
      render: (_, record) => (
        <SText text={record.product?.uom || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("product.materialProcess")} />,
      key: "materialProcess",
      width: 160,
      render: (_, record) => (
        <SText text={record.product?.process || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("warehouse.count")} />,
      key: "stockCount",
      width: 160,
      hidden: open !== "ADJUST",
      render: (_, record) => (
        <SText text={numFormmater(record.stockCount || 0)} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("counting.quantity")} />,
      key: "count",
      width: 160,
      fixed: "right",
      render: (_, record) => (
        <InputNumber changeOnWheel value={record.count} max={open === "ADJUST" ? record.stockCount : undefined}
          onChange={val => setItems(last => last.map(p => ({ ...p, count: p.productId === record.productId ? val || 0 : p.count })))}
          precision={0}
          min={1}
        />
      )
    },
  ];

  const headerChildren = (
    <Flex gap={10} style={{ marginBottom: 16 }}>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => setOpenProductSelect(true)}
      >
        {t("warehouse.addProduct")}
      </Button>
      <Upload
        accept=".xlsx,.xls"
        beforeUpload={handleImport}
        showUploadList={false}
        disabled={uploading}
      >
        <Button loading={uploading}>{t("requirement.inputTemp")}</Button>
      </Upload>
      <Button
        onClick={handleSaveTemplate}
      >
        {t('requirement.downloadTemplate')}
      </Button>
      <Button
        danger
        icon={<DeleteOutlined />}
        onClick={handleDeleteSelected}
        disabled={selectedRowKeys.length === 0}
      >
        {t("warehouse.delete")}
      </Button>
    </Flex>
  )
  return (
    <Modal title={open === "ADD" ? t('warehouse.inboundOprt') : open === "ADJUST" ? t('warehouse.outboundOprt') : t('counting.startInv')} open={!!open}
      width={1000} maskClosable={false} onCancel={onClose} onOk={handleOk}
      okButtonProps={{ loading }}
    >
      <Spin spinning={uploading || loading} >
        <Flex vertical style={{ height: "70vh" }} gap={4}>
          <Form.Item layout="vertical" label={t('warehouse.sourceType')} style={{ marginBottom: 0 }}>
            <Select value={type} onChange={setType} options={SourceTypeList} placeholder={t('warehouse.sourceType')} />
          </Form.Item>
          <Form.Item layout="vertical" label={t('warehouse.remark')} style={{ marginBottom: 0 }}>
            <Input.TextArea value={desc} onChange={e => setDesc(e.target.value)} placeholder={t('warehouse.remark')} maxLength={1000} showCount autoSize={{ minRows: 2, maxRows: 2 }} />
          </Form.Item>
          <Typography.Title level={5} style={{ margin: 0 }}>{t('counting.item')}</Typography.Title>
          <STableLayout
            columns={columns as Array<AnyObject>}
            headerChildren={headerChildren}
            total={items.length}
            dataSource={items}
            pagination={false}
            extHeight={100}
            rowKey={"productId"}
            rowSelection={{
              selectedRowKeys,
              onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
            }}
          />
          {open === "ADJUST" ? (
            <SelectFactory
              open={openProductSelect}
              onClose={() => setOpenProductSelect(false)}
              onConfirm={handleAddOut}
            />
          )
            : (
              <ProductSelectMulti
                openProduct={openProductSelect}
                setOpenProduct={setOpenProductSelect}
                values={items.map(i => i.product)}
                handleOk={handleAddProducts}
                isQuantity={false}
              />
            )}
        </Flex>
      </Spin>
    </Modal>
  )
};

export default AddFacInventory;